<script setup lang="ts">
/**
 * A content-scope tag with its meaning attached (ADR 0010).
 *
 * "contracts", "risma", "offerings", "cases" are opaque on a table row —
 * they tell you the assistant is partitioned, not what the partition holds
 * or which system it came from. Hovering (or focusing, for keyboard users)
 * shows a card with the four things worth knowing: what it is, which system
 * it comes from, where, and who can actually retrieve it.
 *
 * Two deliberate restraints, both learned the hard way:
 *
 * 1. **No `title` attribute.** An earlier version set one alongside this
 *    card, which meant the browser drew its own native tooltip about a
 *    second later, on top of the card, unstyled. One tooltip per element.
 *    `aria-label` carries the text for screen readers without rendering.
 *
 * 2. **Short copy only.** The card reads `summary` and `access` — one line
 *    each — not the registry's `description`/`caveat`, which are the long
 *    form for whoever opens the YAML. A tooltip holding three paragraphs is
 *    a tooltip nobody finishes.
 *
 * `status: reserved` and `documented: false` are surfaced rather than
 * hidden: a tag nothing ingests into makes a corpus LOOK available when it
 * is not, which is exactly what the card should catch.
 */
import { computed, ref } from "vue";
import type { ScopeTagInfo } from "../api/admin";

const props = defineProps<{ tag: string; info?: ScopeTagInfo }>();

const open = ref(false);

const reserved = computed(() => props.info?.status === "reserved");
const undocumented = computed(() => props.info != null && !props.info.documented);

/** Screen-reader text. Same facts as the card, flattened to one string. */
const plain = computed(() => {
  const i = props.info;
  if (!i) return props.tag;
  if (!i.documented) return `${props.tag} — not described in scope-tags.yaml`;
  return [i.label, i.summary, i.access].filter(Boolean).join(". ");
});
</script>

<template>
  <span
    class="scope-tag"
    :aria-label="plain"
    tabindex="0"
    @mouseenter="open = true"
    @mouseleave="open = false"
    @focus="open = true"
    @blur="open = false"
  >
    <span
      class="pill pill--scope"
      :class="{
        'pill--reserved': reserved,
        'pill--undocumented': undocumented,
      }"
      >{{ tag }}</span
    >
    <span v-if="open && info" class="scope-tag__card" role="tooltip">
      <template v-if="info.documented">
        <span class="scope-tag__head">
          <strong>{{ info.label || tag }}</strong>
          <span v-if="info.system" class="scope-tag__system">{{
            info.system
          }}</span>
          <span v-if="reserved" class="badge badge--reserved">reserved</span>
        </span>
        <span v-if="info.summary" class="scope-tag__desc">{{
          info.summary
        }}</span>
        <span v-if="info.where" class="scope-tag__row">
          <span class="k">From</span><span class="v">{{ info.where }}</span>
        </span>
        <span v-if="info.access" class="scope-tag__row">
          <span class="k">Access</span><span class="v">{{ info.access }}</span>
        </span>
      </template>
      <template v-else>
        <span class="scope-tag__head">
          <strong>{{ tag }}</strong>
          <span class="badge badge--undoc">undocumented</span>
        </span>
        <span class="scope-tag__desc">
          Not described in <code>config/scope-tags.yaml</code>.
        </span>
      </template>
    </span>
  </span>
</template>

<style scoped>
.scope-tag {
  position: relative;
  display: inline-block;
  cursor: help;
}
/* The pill styling lives here, not in the parent table: scoped CSS reaches
   a child component's ROOT element only, so `.admin-table__acl .pill--scope`
   in AdminAssistants no longer matches the inner span once the tag became a
   component. Same visual result, owned by the thing that renders it. */
.scope-tag .pill {
  display: inline-block;
  padding: 0.1rem 0.5rem;
  margin: 0 0.25rem 0.2rem 0;
  font-family: var(--font-mono, monospace);
  font-size: 0.78rem;
  border-radius: 999px;
  background: color-mix(in srgb, var(--color-ink) 6%, transparent);
  color: var(--color-muted);
}
.scope-tag .pill--scope {
  background: var(--color-accent-dim);
  color: var(--color-accent);
  /* A dotted underline is the standard "there is an explanation here"
     affordance; without it nothing on the row suggests hovering. */
  border-bottom: 1px dotted currentColor;
}
/* A reserved tag looks available on the row but retrieves nothing. Muting it
   says so at a glance, before anyone hovers. */
.scope-tag .pill--reserved {
  background: color-mix(in srgb, var(--color-ink) 5%, transparent);
  color: var(--color-muted);
  border-bottom-style: dashed;
}
.scope-tag .pill--undocumented {
  border-bottom-color: #d98324;
}
.scope-tag:focus {
  outline: none;
}
.scope-tag:focus-visible .pill {
  outline: 2px solid var(--color-accent);
  outline-offset: 1px;
}
.scope-tag__card {
  position: absolute;
  left: 0;
  top: calc(100% + 6px);
  z-index: 60;
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: max-content;
  max-width: 22rem;
  padding: 0.6rem 0.75rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: 0 8px 24px rgb(0 0 0 / 0.14);
  font-size: 0.8rem;
  line-height: 1.4;
  color: var(--color-ink);
  white-space: normal;
  text-align: left;
}
.scope-tag__head {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.4rem;
}
.scope-tag__system {
  color: var(--color-muted);
  font-size: 0.75rem;
}
.badge {
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.05rem 0.35rem;
  border-radius: 3px;
  font-weight: 700;
}
.badge--reserved {
  background: color-mix(in srgb, var(--color-ink) 8%, transparent);
  color: var(--color-muted);
}
.badge--undoc {
  background: color-mix(in srgb, #d98324 18%, transparent);
  color: #a4611a;
}
.scope-tag__desc {
  color: var(--color-ink);
}
.scope-tag__row {
  display: flex;
  gap: 0.5rem;
}
.scope-tag__row .k {
  color: var(--color-muted);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  flex: 0 0 3.2rem;
  padding-top: 1px;
}
.scope-tag__row .v {
  flex: 1 1 auto;
  word-break: break-word;
}
</style>
